import { useState, useCallback, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiAlertCircle } = FiIcons;

/**
 * Custom hook for managing button states with 4-state machine
 * States: 'idle' | 'loading' | 'success' | 'failed'
 *
 * @param {Object} config - Configuration object
 * @param {string} config.idleText - Text to show in idle state
 * @param {string} config.loadingText - Text to show during loading
 * @param {string} config.successText - Text to show on success
 * @param {string} config.failedText - Text to show on failure
 * @param {Function} config.onSuccess - Callback when action succeeds
 * @param {Function} config.onError - Callback when action fails
 * @param {number} config.autoResetDelay - Auto-reset delay in ms (default: 2000)
 * @param {React.Component} config.idleIcon - Icon for idle state
 * @param {React.Component} config.loadingIcon - Icon for loading state (same as idle, will spin)
 * @returns {Object} Button state utilities
 */
export const useButtonState = ({
  idleText = 'Submit',
  loadingText = 'Processing...',
  successText = 'Saved!',
  failedText = 'Failed',
  onSuccess = null,
  onError = null,
  autoResetDelay = 2000,
  idleIcon = null,
  loadingIcon = null,
} = {}) => {
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null);

  // Clear any existing timeout
  const clearAutoReset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Auto-reset to idle after delay
  const resetToIdle = useCallback(() => {
    clearAutoReset();
    timeoutRef.current = setTimeout(() => {
      setState('idle');
      setError(null);
    }, autoResetDelay);
  }, [clearAutoReset, autoResetDelay]);

  // Handle async action
  const handleAction = useCallback(
    async (actionFn) => {
      try {
        // Clear any pending reset
        clearAutoReset();
        setError(null);

        // Set loading state
        setState('loading');

        // Execute action
        await actionFn();

        // Set success state
        setState('success');

        // Call success callback if provided
        if (onSuccess) {
          onSuccess();
        }

        // Auto-reset to idle
        resetToIdle();
      } catch (err) {
        // Set failed state
        setState('failed');
        setError(err);

        // Call error callback if provided
        if (onError) {
          onError(err);
        }

        // Auto-reset to idle
        resetToIdle();
      }
    },
    [onSuccess, onError, clearAutoReset, resetToIdle]
  );

  // Manual reset
  const reset = useCallback(() => {
    clearAutoReset();
    setState('idle');
    setError(null);
  }, [clearAutoReset]);

  // Get appropriate icon for current state
  const getIcon = useCallback(() => {
    switch (state) {
      case 'loading':
        return loadingIcon || idleIcon;
      case 'success':
        return FiCheck;
      case 'failed':
        return FiAlertCircle;
      default:
        return idleIcon;
    }
  }, [state, idleIcon, loadingIcon]);

  // Get appropriate text for current state
  const getText = useCallback(() => {
    switch (state) {
      case 'loading':
        return loadingText;
      case 'success':
        return successText;
      case 'failed':
        return failedText;
      default:
        return idleText;
    }
  }, [state, idleText, loadingText, successText, failedText]);

  // Get appropriate CSS classes for current state
  const getStateClasses = useCallback(() => {
    const baseClasses = 'transition-all duration-200';

    switch (state) {
      case 'loading':
        return `${baseClasses} opacity-80 cursor-wait`;
      case 'success':
        return `${baseClasses} bg-green-500 hover:bg-green-600 text-white`;
      case 'failed':
        return `${baseClasses} bg-red-500 hover:bg-red-600 text-white`;
      default:
        return baseClasses;
    }
  }, [state]);

  // Cleanup timeout on unmount
  useState(() => {
    return () => clearAutoReset();
  });

  return {
    state,
    error,
    isLoading: state === 'loading',
    isSuccess: state === 'success',
    isFailed: state === 'failed',
    isIdle: state === 'idle',
    handleAction,
    reset,
    icon: getIcon(),
    text: getText(),
    classes: getStateClasses(),
  };
};
